import { useTranslation } from "react-i18next";
import {
  Box,
  Divider,
  Flex,
  useDisclosure,
  useToast,
  useBreakpointValue,
  Button
} from "@chakra-ui/react";
import { Card } from "../../components/Card";
import { DecarbonizationChart, ExportModal, ParametersModal } from "./components";
import React, { useEffect, useRef, useState } from "react";
import { CHART_COLORS, getRandomColor, VALIDATION_PATTERNS } from "./helpers";
import {
  useLoadLevers,
  useExportDecarbonizationData,
  useGetChartData,
  useGetDecarbonization, useUpdateDecarbonization, useUpdateEstimatedGrowth, useUpdatePathwaySelection
} from "../../hooks/query";
import { showErrorToast, useAsyncDebounce } from "../../utils";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { Pathways } from "./components/Pathways";
import { NumericFormInputGroup } from "../../components/Forms";
import { useForm } from "react-hook-form";

function Decarbonization() {
  const { t } = useTranslation("translation", {
    keyPrefix: "decarbonization"
  });
  const contentDisplay = useBreakpointValue({
    base: "block",
    xl: "flex"
  });
  const toast = useToast();
  const dispatch = useDispatch();
  const {
    isOpen: isParamsModalOpen,
    onOpen: onParamsModalOpen,
    onClose: onParamsModalClose
  } = useDisclosure();

  const {
    isOpen: isExportModalOpen,
    onOpen: onExportModalOpen,
    onClose: onExportModalClose
  } = useDisclosure();

  const [chartData, setChartData] = useState();
  const [decarbonizationData, setDecarbonizationData] = useState();
  const [modalData, setModalData] = useState();

  const preventCloseModalRef = useRef(false);

  useGetDecarbonization({
    onSuccess: (data) => {
      setDecarbonizationData(data);
    },
    onError: err => showErrorToast(toast, err.message),
    cacheTime: 0
  });

  const {
    refetch: refetchChartData
  } = useGetChartData({
    onSuccess: (data) => {
      let dataElements = [];
      data.data.forEach(el => {
        dataElements = dataElements.concat(el.data);
      });
      const maxValue = Math.max(...dataElements.map(el => el.y));
      const maxYear = Math.max(...dataElements.map(el => Number(el.x)));

      const coloredChartData = data.data.map(el => {
        return {
          ...el,
          color: CHART_COLORS[el.id] ?? getRandomColor()
        };
      });

      const newData = {
        baseYear: data.organization?.baseYear,
        baselineEmissionsTotal: data.baselineEmissionsTotal,
        chartData: coloredChartData,
        maxValue: maxValue + 0.05 * maxValue,
        maxYear: maxYear
      };
      setChartData(newData);
    },
    onError: err => showErrorToast(toast, err.message),
    cacheTime: 0
  });

  const { mutate: updateDecarbonization } = useUpdateDecarbonization({
    onSuccess: (data) => {
      setDecarbonizationData(data);
      refetchChartData();
      if (preventCloseModalRef.current !== true) {
        handleParamsModalClose();
      } else {
        setModalData(data.levers);
      }
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: updatePathwaySelection } = useUpdatePathwaySelection({
    onSuccess: () => {
      refetchChartData();
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message)
  });

  const { mutate: updateEstimatedGrowth } = useUpdateEstimatedGrowth({
    onSuccess: (data) => {
      setDecarbonizationData(data);
      refetchChartData();
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message)
  });

  const { mutate: loadLevers } = useLoadLevers({
    onSuccess: (data) => {
      setDecarbonizationData(data);
      refetchChartData();
      setModalData(data.levers);
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const onParamsModalSubmit = (leversData, preventCloseModal) => {
    preventCloseModalRef.current = preventCloseModal;
    const data = {
      ...decarbonizationData,
      ...leversData
    };
    dispatch(showAppSpinner());
    updateDecarbonization(data);
  };

  const onLoadLeverClicked = () => {
    dispatch(showAppSpinner());
    loadLevers();
  };

  const handleModalOpen = () => {
    if (decarbonizationData && decarbonizationData.levers) {
      setModalData(decarbonizationData.levers);
      onParamsModalOpen();
    }
  };

  const handleParamsModalClose = () => {
    onParamsModalClose();
    setModalData(null);
    preventCloseModalRef.current = false;
  };

  const handleSwitchChange = (switchData) => {
    updatePathwaySelection(switchData);
  };

  const {
    register,
    formState: { errors: estimatedGrowthErrors },
    reset
  } = useForm({
    mode: "onChange",
    defaultValues: { estimatedYearlyEmissionsGrowth: decarbonizationData?.estimatedYearlyEmissionsGrowth ?? "" }
  });

  useEffect(() => {
    if (decarbonizationData) {
      reset({ estimatedYearlyEmissionsGrowth: decarbonizationData.estimatedYearlyEmissionsGrowth });
    }
  }, [reset, decarbonizationData]);

  const delayUpdate = useAsyncDebounce((value) => {
    if (Object.keys(estimatedGrowthErrors).length === 0) {
      updateEstimatedGrowth(value);
    }
  }, 500);

  const estimatedGrowthRegister = {
    ...register("estimatedYearlyEmissionsGrowth", {
      required: VALIDATION_PATTERNS.DECIMAL_PERCENTAGE.message,
      pattern: VALIDATION_PATTERNS.DECIMAL_PERCENTAGE
    })
  };

  const handleExportClick = () => {
    onExportModalOpen();
  };

  const handleExportModalClose = () => {
    onExportModalClose();
  };

  const handleExport = (exportYear) => {
    dispatch(showAppSpinner());
    exportDecarbonizationData(exportYear);
    onExportModalClose();
  };

  const handleErrors = (err) => {
    const parsedErrorObj = JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)));
    showErrorToast(toast, parsedErrorObj?.message ?? err.message);
    dispatch(hideAppSpinner());
  };

  const {
    mutate: exportDecarbonizationData
  } = useExportDecarbonizationData( {
    onError: err => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  return <>
    <Box marginBlockStart={5}>
      <Flex alignItems="end" justifyContent="space-between">
        <NumericFormInputGroup
          {...estimatedGrowthRegister}
          label={t("page.estimatedYearlyEmissionsGrowth")}
          labelFontSize="md"
          errors={estimatedGrowthErrors}
          type="number"
          addonText="%"
          width="100px"
          backgroundColor="white"
          onChange={(e) => {
            estimatedGrowthRegister.onChange(e);
            // Sending the update request to the server, but delayed with 500 ms (after the last keystroke)
            delayUpdate(e.target.value);
          }}
        />
        <Button
          colorScheme="customScheme"
          type="submit"
          width="32"
          onClick={handleExportClick}
        >
          {t("page.export")}
        </Button>
      </Flex>
      <Divider marginBlock={5} />
      <Flex display={contentDisplay} gap={5}>
        <Card width={contentDisplay === "block" ? "100%" : "45%"}>
          {decarbonizationData &&
            <Pathways
              data={decarbonizationData}
              handleSwitchChange={handleSwitchChange}
              handleModalOpen={handleModalOpen}
            />}
        </Card>
        {chartData && <DecarbonizationChart
          width={contentDisplay === "block" ? "100%" : "55%"}
          blockSize={96}
          marginBlockStart={5}
          data={chartData}
          legends={[
            {
              anchor: "top-right",
              direction: "column",
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 0,
              itemDirection: "left-to-right",
              itemWidth: 100,
              itemHeight: 34,
              itemOpacity: 1,
              symbolSize: 12,
              symbolShape: "circle",
              symbolBorderColor: "rgba(9, 9, 9, .5)",
              effects: [
                {
                  on: "hover",
                  style: {
                    itemBackground: "rgba(0, 0, 0, .03)",
                    itemOpacity: 1
                  }
                }
              ]
            }
          ]}
        />}
      </Flex>
    </Box>
    {modalData && chartData && chartData.baseYear &&
      <ParametersModal
        modalData={modalData}
        baseYear={chartData.baseYear}
        isOpen={isParamsModalOpen}
        onClose={handleParamsModalClose}
        onSubmit={onParamsModalSubmit}
        onLoadLeverClick={onLoadLeverClicked}
      />}
    {decarbonizationData && <ExportModal
      baseYear={decarbonizationData.baseYear}
      isOpen={isExportModalOpen}
      onClose={handleExportModalClose}
      onSubmit={handleExport}
    />}
  </>;
}

export default Decarbonization;